export function mergeCategorySettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        dataZoom: settings?.dataZoom,
        areaDefault: settings?.areaDefault,
        smoothDefault: settings?.smoothDefault,
        showSymbolDefault: settings?.showSymbolDefault,
        synchronizeAxisBounds: settings?.synchronizeAxisBounds,
        barWidth: settings?.barWidth,
        barCategoryGap: settings?.barCategoryGap,
        barGap: settings?.barGap, // 传递barGap配置
    };
}
export function mergeScatterSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        visualMap: settings?.visualMap,
        showRegressionLine: settings?.showRegressionLine,
    };
}
export function mergeHeatmapSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        visualMap: settings?.visualMap,
        cellBorderColor: settings?.cellBorderColor,
    };
}
export function mergeGeoHeatmapSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        visualMap: settings?.visualMap,
        geo: settings?.geo,
    };
}
export function mergeRadarSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        shape: settings?.shape,
        splitNumber: settings?.splitNumber,
        showSymbol: settings?.showSymbol,
        areaDefault: settings?.areaDefault,
    };
}
export function mergeGaugeSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        style: settings?.style,
        min: settings?.min,
        max: settings?.max,
        startAngle: settings?.startAngle,
        endAngle: settings?.endAngle,
        splitNumber: settings?.splitNumber,
        showPointer: settings?.showPointer,
        showProgress: settings?.showProgress,
    };
}
const LEGEND_MARGIN = 16; // 图例离外边距
const LEGEND_VERTICAL_CENTER = "center";
function resolveLegendOrientation(position) {
    if (!position) {
        return { orient: "horizontal", top: LEGEND_MARGIN, left: "center" };
    }
    switch (position) {
        case "left":
            return { orient: "vertical", left: LEGEND_MARGIN, top: LEGEND_VERTICAL_CENTER };
        case "right":
            return { orient: "vertical", right: LEGEND_MARGIN, top: LEGEND_VERTICAL_CENTER };
        case "bottom":
            return { orient: "horizontal", bottom: LEGEND_MARGIN, left: "center" };
        default:
            return { orient: "horizontal", top: LEGEND_MARGIN, left: "center" };
    }
}
export function buildLegend(dataLegend, settingLegend) {
    const merged = {
        show: true,
        ...dataLegend,
        ...settingLegend,
    };
    if (merged.show === false) {
        return { show: false };
    }
    const orientation = resolveLegendOrientation(merged.position);
    const config = {
        show: true,
        orient: merged.orient ?? orientation.orient,
        // 图标和间距设置（默认值，可被外部配置覆盖）
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 8,
        // 自动换行（水平图例时）
        width: orientation.orient === "horizontal" ? "auto" : undefined,
        // 鼠标移上去时显示 tooltip（显示完整名称）
        tooltip: {
            show: true,
            formatter: (params) => {
                // params.name 包含原始的系列名称
                return params.name || "";
            },
        },
        // 选中状态下的样式
        selectedMode: true,
    };
    // 处理图例文字显示
    // 优先级：外部 formatter > showText > 默认（不显示文字）
    if (merged.formatter !== undefined) {
        // 如果外部提供了 formatter，使用外部的（支持函数或字符串）
        config.formatter = merged.formatter;
    }
    else if (merged.showText === true) {
        // 如果 showText 为 true，不设置 formatter，使用 ECharts 默认行为（显示文字）
        // 不设置 formatter 即可
    }
    else {
        // 默认 Tag 模式：不显示文字（不占空间），hover 时通过 tooltip 显示
        config.formatter = () => "";
    }
    Object.assign(config, orientation);
    if (merged.position === "left") {
        config.align = "left";
    }
    else if (merged.position === "right") {
        config.align = "right";
    }
    // 允许外部传入其他 ECharts 图例配置项（如 textStyle、itemGap 等）
    // 这些配置会覆盖默认值
    const externalConfig = { ...dataLegend, ...settingLegend };
    const allowedExternalKeys = [
        "textStyle",
        "itemWidth",
        "itemHeight",
        "itemGap",
        "itemStyle",
        "selectedMode",
        "data",
        "width",
        "height",
        "padding",
        "borderRadius",
        "borderColor",
        "borderWidth",
        "backgroundColor",
        "shadowBlur",
        "shadowColor",
        "shadowOffsetX",
        "shadowOffsetY",
    ];
    allowedExternalKeys.forEach((key) => {
        if (externalConfig[key] !== undefined) {
            config[key] = externalConfig[key];
        }
    });
    return config;
}
/** 统一默认：字体 12、字重 300、不裁切，便于所有图表 tooltip 一致 */
const DEFAULT_TOOLTIP_TEXT_STYLE = { fontSize: 12, fontWeight: 300 };
export function buildTooltip(dataTooltip, settingTooltip) {
    const merged = {
        enable: true,
        trigger: "axis",
        ...dataTooltip,
        ...settingTooltip,
    };
    if (merged.enable === false) {
        return { show: false };
    }
    const config = {
        show: true,
        trigger: merged.trigger ?? "axis",
        textStyle: { ...DEFAULT_TOOLTIP_TEXT_STYLE, ...merged.textStyle },
        confine: merged.confine ?? false,
        borderWidth: 0,
    };
    if (merged.formatter) {
        config.formatter = merged.formatter;
    }
    if (merged.position) {
        config.position = merged.position;
    }
    if (merged.axisPointer !== undefined) {
        config.axisPointer = merged.axisPointer;
    }
    return config;
}
export function buildDataZoom(dataZoom, settingZoom) {
    const merged = {
        enable: false,
        start: 0,
        end: 100,
        ...settingZoom,
        ...dataZoom,
    };
    if (!merged.enable) {
        return undefined;
    }
    return [
        {
            type: "inside",
            start: merged.start,
            end: merged.end,
        },
        {
            type: "slider",
            start: merged.start,
            end: merged.end,
        },
    ];
}
export function mergePieSettings(settings) {
    return {
        legend: settings?.legend,
        tooltip: settings?.tooltip,
        radius: settings?.radius,
        roseType: settings?.roseType,
        showLabels: settings?.showLabels,
        labelLine: settings?.labelLine,
        explode: settings?.explode,
        cornerRadius: settings?.cornerRadius,
        labelContent: settings?.labelContent,
        sliceGap: settings?.sliceGap,
    };
}
export function buildVisualMap(dataVisualMap, settingVisualMap, fallback) {
    const merged = {
        show: true,
        calculable: true,
        ...dataVisualMap,
        ...settingVisualMap,
    };
    if (merged.show === false) {
        return { show: false };
    }
    const config = {
        show: merged.show ?? true,
        min: merged.min ?? fallback?.min ?? 0,
        max: merged.max ?? fallback?.max ?? 100,
        calculable: merged.calculable ?? true,
    };
    if (merged.inRangeColors?.length) {
        config.inRange = { color: merged.inRangeColors };
    }
    return config;
}
